import { useIntl } from 'react-intl';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import InputAdornment from '@mui/material/InputAdornment';
import { useFormReadOnly } from '../../components/formReadOnly';
import TabSuggestAutocomplete from '../../components/TabSuggestAutocomplete';
import { hintsFilter } from '../../components/hintsFilter';

/** A round colour chip — the Munsell reading's colour, shown inline. */
export function Swatch({ hex }: { hex: string }) {
    return (
        <Box
            component="span"
            sx={{
                display: 'block',
                flexShrink: 0,
                width: 16,
                height: 16,
                borderRadius: '50%',
                bgcolor: hex,
                border: 1,
                borderColor: 'divider'
            }}
        />
    );
}

interface VocabSuggestFieldProps {
    value: string;
    onChange: (next: string) => void;
    /** The controlled vocabulary offered; free text is still allowed. */
    options: readonly string[];
    labelId: string;
    /** Secondary text per option (e.g. a Munsell code's plain-language name). */
    hints?: Record<string, string>;
    /** Maps a value to a colour, drawn as a swatch in the list and the input. */
    dotFor?: (value: string) => string | null;
}

/**
 * A free-text field with a controlled vocabulary behind it: the options are
 * suggestions, not a constraint, because the field vocabularies are starter
 * sets and an excavation will meet words they do not list.
 *
 * Lifted out of the Planum form so the Profil form can use the same editors —
 * a Befund that is only visible in section records its Bodenmerkmale there.
 */
export default function VocabSuggestField({
    value,
    onChange,
    options,
    labelId,
    hints,
    dotFor
}: VocabSuggestFieldProps) {
    // Follows the surrounding form (see formReadOnly).
    const readOnly = useFormReadOnly();
    const intl = useIntl();
    const currentDot = dotFor?.(value) ?? null;
    return (
        <TabSuggestAutocomplete
            freeSolo
            fullWidth
            readOnly={readOnly}
            options={options as string[]}
            inputValue={value}
            onInputChange={(_event, next) => onChange(next)}
            onTake={onChange}
            filterOptions={hints ? hintsFilter(hints) : undefined}
            renderOption={
                hints || dotFor
                    ? (props, option) => {
                          const dot = dotFor?.(option) ?? null;
                          return (
                              <Box
                                  component="li"
                                  {...props}
                                  key={option}
                                  sx={{ gap: 1 }}
                              >
                                  {dot && <Swatch hex={dot} />}
                                  {option}
                                  {hints && (
                                      <Typography
                                          component="span"
                                          variant="body2"
                                          sx={{
                                              color: 'text.secondary',
                                              ml: 'auto'
                                          }}
                                      >
                                          {hints[option] ?? ''}
                                      </Typography>
                                  )}
                              </Box>
                          );
                      }
                    : undefined
            }
            renderInput={(params) => (
                <TextField
                    {...params}
                    label={intl.formatMessage({ id: labelId })}
                    slotProps={{
                        ...params.slotProps,
                        input: {
                            ...params.slotProps.input,
                            startAdornment: currentDot ? (
                                <InputAdornment
                                    position="start"
                                    // Drop the default 16px top margin (meant to
                                    // clear a floating label) so the dot sits
                                    // centred on the input line.
                                    sx={{ mt: '0 !important' }}
                                >
                                    <Swatch hex={currentDot} />
                                </InputAdornment>
                            ) : undefined
                        }
                    }}
                />
            )}
        />
    );
}
